import type { Issue } from "contracts/api/issue/responses";
import { ISSUE_SEVERITY } from "contracts/enums/core";
import { CORE_WEB_VITALS_CONFIDENCE_LEVEL } from "contracts/enums/web-vitals";
import { JOURNEY_FRICTION_KIND } from "../../enums/scan";
import type { JourneyFriction, PriorityFlowConcern } from "./journeyTypes";
import type { ConcernCandidate } from "./priorityFlowConcerns";

/**
 * The order priority-flow concerns are read in: worst friction first, then
 * worst issue. Kept beside the builder rather than inside it so the ranking
 * rules can be read as one list.
 */

export function compareCandidates(
  left: ConcernCandidate,
  right: ConcernCandidate,
): number {
  return (
    concernOrder(left.friction) - concernOrder(right.friction) ||
    compareIssuesNullable(left.friction.issue, right.friction.issue) ||
    left.journey.rank - right.journey.rank ||
    left.friction.key.localeCompare(right.friction.key)
  );
}

export function compareConcerns(
  left: PriorityFlowConcern,
  right: PriorityFlowConcern,
): number {
  return (
    concernOrder(left.friction) - concernOrder(right.friction) ||
    left.journey.rank - right.journey.rank ||
    left.key.localeCompare(right.key)
  );
}

function compareIssuesNullable(
  left: Issue | null,
  right: Issue | null,
): number {
  if (left && right) return compareIssues(left, right);
  if (left) return -1;
  if (right) return 1;
  return 0;
}

export function compareIssues(left: Issue, right: Issue): number {
  return (
    severityOrder(left.severity) - severityOrder(right.severity) ||
    right.pages.length - left.pages.length ||
    left.id.localeCompare(right.id)
  );
}

function concernOrder(friction: JourneyFriction): number {
  if (friction.kind === JOURNEY_FRICTION_KIND.BROKEN_LINK) return 0;
  if (friction.issue?.severity === ISSUE_SEVERITY.CRITICAL) return 1;
  if (friction.issue?.severity === CORE_WEB_VITALS_CONFIDENCE_LEVEL.HIGH)
    return 2;
  if (friction.kind === JOURNEY_FRICTION_KIND.NO_ONWARD_LINK) return 3;
  if (friction.issue?.severity === ISSUE_SEVERITY.MEDIUM) return 4;
  if (friction.issue) return 5;
  return 6;
}

function severityOrder(severity: Issue["severity"]): number {
  return { critical: 0, high: 1, medium: 2, low: 3 }[severity];
}
